import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setActivityTogglePopup,
  setActivityTogglePopupTwo,
} from "../../../core/data/redux/commonSlice";
import { all_routes } from "../../router/all_routes";
import { fetchAllProposals, fetchProposalsByUserId, ProposalResponse } from "../../../api/proposal";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import { highlightMatch } from "../../../core/common/highlightmatch";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import { RootState } from "../../../core/data/redux/store";

const Proposalsgrid = () => {
  const route = all_routes;
  const [proposals, setProposals] = useState<ProposalResponse[]>([]);
   const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [searchText, setSearchText] = useState("");
  const [sortOption, setSortOption] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(end.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    return [start, end];
  });

  // ✅ Handle sort click
  const handleSortOption = (option: string) => {
    setSortOption(option);
  };

  // ✅ Handle status filter click
  const toggleStatusFilter = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // ✅ Apply filtering, searching, sorting
  const filteredAndSortProposals = useMemo(() => {
    let result = [...proposals];

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter((proposal) =>
        [
          proposal.description,
          proposal.contact_name,
          proposal.status,
          proposal.proposal_id,
        ].some((field) => field?.toLowerCase().includes(lower))
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter((proposal) =>
        statusFilter.includes(proposal.status.toLowerCase())
      );
    }

    // ✅ Date Range Filter
    if (dateRange[0] && dateRange[1]) {
      result = result.filter((proposal) => {
        const proposalDate = new Date(proposal.Date_of_proposal);
        return proposalDate >= dateRange[0] && proposalDate <= dateRange[1];
      });
    }

    if (sortOption === "asc") {
      result.sort((a, b) => a.proposal_id.localeCompare(b.proposal_id));
    } else if (sortOption === "desc") {
      result.sort((a, b) => b.proposal_id.localeCompare(a.proposal_id));
    } else if (sortOption === "recentlyAdded") {
      result.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    } else if (sortOption === "recentlyViewed") {
      result.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    }
    return result;
  }, [proposals, searchText, sortOption, statusFilter, dateRange]);

const userId = useSelector((state: RootState) => state.auth.user_id);
useEffect(() => {
  const getProposals = async () => {
    setLoading(true);
    setError(null);
    try {
      if (userId) {
        const data = await fetchProposalsByUserId(userId);
        setProposals(data);
      }
    } catch (err) {
      setError("Failed to fetch proposals");
    } finally {
      setLoading(false);
    }
  };
  getProposals();
}, [userId]);

  // Extract unique filter options from proposals
  const getUniqueValues = (
    key: keyof ProposalResponse | ((item: ProposalResponse) => string)
  ) => {
    const values = proposals.map((item) =>
      typeof key === "function" ? key(item) : item[key] || "N/A"
    );
    return Array.from(new Set(values))
      .filter((value) => value !== "N/A")
      .sort();
  };

  function getBadgeColor(status: string) {
    switch (status.toLowerCase()) {
      case "draft":
        return "secondary";
      case "accepted":
        return "success";
      case "declined":
        return "danger";
      case "pending":
        return "warning";
      default:
        return "info";
    }
  }

  const getStatusCounts = (): Record<string, number> => {
    const counts: Record<string, number> = {};
    proposals.forEach((proposal) => {
      const status = proposal.status.toLowerCase();
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div>
      <>
          <PageLoadProgressBar />
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                {/* Page Header */}
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">
                        <i className="ti ti-file-star text-primary me-2" />
                        Quotes
                        <span className="count-title">{proposals.length}</span>
                      </h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <Link
                          to={route.ProposalsGrid}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-original-title="Refresh"
                        >
                          <i className="ti ti-refresh-dot" />
                        </Link>
                        <Link
                          to="#"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-original-title="Collapse"
                          id="collapse-header"
                        >
                          <i className="ti ti-chevrons-up" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                  <ul className="contact-breadcrumb">
                    <li>
                      <Link to={route.ProposalsList}>
                        <i className="ti ti-arrow-narrow-left" />
                        Quotes
                      </Link>
                    </li>
                  </ul>
                </div>
                </div>
                {/* /Page Header */}
                {/* Card */}
                <div className="card">
                  <div className="card-header">
                    {/* Search */}
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            <i className="ti ti-search" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search Quotes"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-sm-8"></div>
                    </div>
                    {/* /Search */}
                  </div>
                  <div className="card-body">
                    {/* Filter */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-filter me-2" />
                            {statusFilter.length === 0
                              ? "All Quotes"
                              : statusFilter.join(", ")}
                          </Link>

                          <div className="dropdown-menu dropdown-menu-start">
                            <ul className="list-unstyled mb-0">
                              {/* All Proposals */}
                              <li>
                                <Link
                                  to="#"
                                  onClick={() => setStatusFilter([])}
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${statusFilter.length === 0
                                      ? "fw-bold text-primary"
                                      : ""
                                    }`}
                                >
                                  <span>
                                    <i className="ti ti-dots-vertical me-2" />
                                    All Quotes
                                  </span>
                                  <span className="d-flex align-items-center gap-2">
                                    <span className="badge bg-light text-dark">
                                      {proposals.length}
                                    </span>
                                    {statusFilter.length === 0 && (
                                      <i className="ti ti-check" />
                                    )}
                                  </span>
                                </Link>
                              </li>

                              <li>
                                <hr className="dropdown-divider" />
                              </li>

                              {/* Status items with count */}
                              {["Draft", "Accepted", "Declined"].map(
                                (status) => {
                                  const isActive =
                                    statusFilter.includes(status);
                                  const count = statusCounts[status] || 0;
                                  return (
                                    <li key={status}>
                                      <Link
                                        to="#"
                                        className={`dropdown-item d-flex justify-content-between align-items-center ${isActive ? "fw-bold text-primary" : ""
                                          }`}
                                        onClick={() =>
                                          toggleStatusFilter(status)
                                        }
                                      >
                                        <span>
                                          <i className="ti ti-dots-vertical me-2" />
                                          {status.charAt(0).toUpperCase() +
                                            status.slice(1)}
                                        </span>
                                        <span className="d-flex align-items-center gap-2">
                                          <span className="badge bg-light text-dark">
                                            {count}
                                          </span>
                                          {isActive && (
                                            <i className="ti ti-check" />
                                          )}
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                }
                              )}
                            </ul>
                          </div>
                        </div>

                        <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-sort-ascending-2 me-2" />
                            Sort
                          </Link>
                          <div className="dropdown-menu dropdown-menu-start">
                            <ul className="list-unstyled mb-0">
                              {[
                                { label: "Ascending", value: "asc" },
                                { label: "Descending", value: "desc" },
                                {
                                  label: "Recently Viewed",
                                  value: "recentlyViewed",
                                },
                                {
                                  label: "Recently Added",
                                  value: "recentlyAdded",
                                },
                              ].map(({ label, value }) => {
                                const isActive = sortOption === value;

                                return (
                                  <li key={value}>
                                    <Link
                                      to="#"
                                      className={`dropdown-item d-flex justify-content-between align-items-center ${isActive ? "fw-bold text-primary" : ""
                                        }`}
                                      onClick={() => handleSortOption(value)}
                                    >
                                      <span>
                                        <i className="ti ti-circle-chevron-right me-1" />
                                        {label}
                                      </span>
                                      {isActive && (
                                        <i className="ti ti-check" />
                                      )}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>

                        <div className="icon-form">
                          <span className="form-icon">
                            <i className="ti ti-calendar" />
                          </span>

                          <DateRangePicker
                            initialSettings={initialSettings}
                            onApply={(event, picker) => {
                              setDateRange([
                                picker.startDate.toDate(),
                                picker.endDate.toDate(),
                              ]);
                            }}
                          >
                            <input
                              className="form-control bookingrange"
                              type="text"
                              placeholder="Select date range"
                              readOnly
                              value={`${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`}
                            />
                          </DateRangePicker>
                        </div>
                      </div>
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <div className="view-icons">
                          <Link to={route.ProposalsList}>
                            <i className="ti ti-list-tree" />
                          </Link>
                          <Link to={route.ProposalsGrid} className="active">
                            <i className="ti ti-grid-dots" />
                          </Link>
                        </div>
                      </div>
                    </div>
                    {/* /Filter */}
                    {/* Proposal Grid */}
                    <div className="row">
                      {filteredAndSortProposals.map((proposal) => (
                        <div
                          className="col-xxl-4 col-xl-4 col-md-6"
                          key={proposal.client_proposal_number}
                        >
                          <div className="card border proposal-card">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3">
                                <div className="flex-shrink-0">
                                  <span className="badge badge-purple-light">
                                    #
                                    {highlightMatch(
                                      proposal.proposal_id?.slice(-6) || "N/A",
                                      searchText
                                    )}
                                  </span>
                                </div>
                              </div>
                              <div className="d-block">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <div>
                                    <h4>
                                      {highlightMatch(
                                        proposal.description || "Quotes",
                                        searchText
                                      )}
                                    </h4>
                                    <p>
                                      Product:{" "}
                                      {highlightMatch(
                                        proposal.leads?.lead_description ||
                                        "N/A",
                                        searchText
                                      )}
                                    </p>
                                  </div>
                                  <div>
                                    <span
                                      className={`badge badge-${getBadgeColor(
                                        proposal.status
                                      )}`}
                                    >
                                      {highlightMatch(
                                        proposal.status || "N/A",
                                        searchText
                                      )}
                                    </span>
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-moneybag fs-12" />
                                    </span>
                                    Total Value:{" "}
                                    {proposal?.proposal_total_amount != null
                                      ? proposal.proposal_total_amount.toLocaleString()
                                      : "N/A"}
                                  </p>

                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-calendar-event fs-12" />
                                    </span>
                                    Date:{" "}
                                    {highlightMatch(
                                      proposal?.Date_of_proposal
                                        ? new Date(
                                          proposal.Date_of_proposal
                                        ).toLocaleDateString()
                                        : "N/A",
                                      searchText
                                    )}
                                  </p>

                                  <p className="d-flex align-items-center">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-calendar-stats fs-12" />
                                    </span>
                                    Open till:{" "}
                                    {highlightMatch(
                                      proposal?.validation_date
                                        ? new Date(
                                          proposal.validation_date
                                        ).toLocaleDateString()
                                        : "N/A",
                                      searchText
                                    )}
                                  </p>

                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-activity-heartbeat fs-12" />
                                    </span>
                                    Logins:{" "}
                                    {highlightMatch(
                                      `${proposal?.proposal_no_logins ?? "N/A"
                                      } × ₹${proposal?.proposal_rate_per_login ??
                                      "N/A"
                                      }`,
                                      searchText
                                    )}
                                  </p>

                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-discount-check fs-12" />
                                    </span>
                                    Discount:{" "}
                                    {highlightMatch(
                                      `${proposal?.proposal_discount_percentage ??
                                      "N/A"
                                      }% (${proposal?.proposal_total_amount?.toLocaleString() ??
                                      "N/A"
                                      } ${proposal?.currency ?? ""})`,
                                      searchText
                                    )}
                                  </p>

                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-percentage fs-12" />
                                    </span>
                                    cgst:{" "}
                                    {highlightMatch(
                                      `${proposal?.cgst_percentage ??
                                      "N/A"
                                      }% (${proposal?.cgst?.toLocaleString() ??
                                      "N/A"
                                      } ${proposal?.currency ?? ""})`,
                                      searchText
                                    )}
                                  </p>
                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-percentage fs-12" />
                                    </span>
                                    sgst:{" "}
                                    {highlightMatch(
                                      `${proposal?.sgst_percentage ??
                                      "N/A"
                                      }% (${proposal?.sgst?.toLocaleString() ??
                                      "N/A"
                                      } ${proposal?.currency ?? ""})`,
                                      searchText
                                    )}
                                  </p>
                                  <p className="d-flex align-items-center mb-2">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-percentage fs-12" />
                                    </span>
                                    igst:{" "}
                                    {highlightMatch(
                                      `${proposal?.igst_percentage ??
                                      "N/A"
                                      }% (${proposal?.igst?.toLocaleString() ??
                                      "N/A"
                                      } ${proposal?.currency ?? ""})`,
                                      searchText
                                    )}
                                  </p>


                                  <p className="d-flex align-items-center mb-0">
                                    <span className="avatar avatar-xs border me-2 text-dark">
                                      <i className="ti ti-clock-hour-4 fs-12" />
                                    </span>
                                    Last updated:{" "}
                                    {highlightMatch(
                                      proposal?.updated_at
                                        ? new Date(
                                          proposal.updated_at
                                        ).toLocaleDateString()
                                        : "N/A",
                                      searchText
                                    )}
                                  </p>
                                </div>
                              </div>

                              <div className="bg-light-400 rounded p-2">
                                <div className="d-flex align-items-center">
                                  <div className="d-flex flex-column">
                                    <span className="d-block">Sent to</span>
                                    <Link to="#" className="text-default">
                                      {highlightMatch(
                                        proposal.contact_name ||
                                        "Unknown Contact",
                                        searchText
                                      )}
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* /Proposal Grid */}
                    <div className="load-btn text-center">
                      <Link to="#" className="btn btn-primary">
                        Load More Contacts
                        <i className="ti ti-loader" />
                      </Link>
                    </div>
                  </div>
                </div>
                {/* /Card */}
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
    </div>
  );
};
export default Proposalsgrid;
